import { NextRequest, NextResponse } from 'next/server'
import { and, eq } from 'drizzle-orm'
import { createTransport } from 'nodemailer'
import { db } from '@/db'
import { auditLog, tenantConfigs, ticketMessages, tickets, users } from '@/db/schema'
import { requireExpertApiUser } from '@/lib/role-guard'
import { transition } from '@/lib/ticket-state-machine'
import { buildResponseNotificationEmail } from '@/lib/response-notification-email'

function sanitize(text: string): string {
  return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

/** Expert follow-up reply after the initial response (AWAITING_REPLY → RESPONDED). */
export async function POST(
  request: NextRequest,
  { params }: { params: Promise<{ ticketId: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { ticketId } = await params
  const body = await request.json().catch(() => null)
  if (!body?.responseText || typeof body.responseText !== 'string') {
    return NextResponse.json({ error: 'responseText is required' }, { status: 400 })
  }

  const responseText = body.responseText.trim()
  if (responseText.length < 5) {
    return NextResponse.json({ error: 'responseText must be at least 5 characters' }, { status: 422 })
  }

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, ticketId), eq(tickets.tenantId, guard.user.tenantId)))
    .limit(1)

  if (!ticket) return NextResponse.json({ error: 'Ticket not found' }, { status: 404 })
  if (ticket.status !== 'AWAITING_REPLY') {
    return NextResponse.json(
      { error: `Cannot reply while ticket is ${ticket.status}` },
      { status: 409 },
    )
  }

  const [message] = await db
    .insert(ticketMessages)
    .values({
      tenantId: guard.user.tenantId,
      ticketId: ticket.id,
      authorId: guard.user.id,
      role: 'expert',
      bodyRaw: responseText,
      bodySanitized: sanitize(responseText),
    })
    .returning()

  const nextStatus = transition(ticket.status, 'RESPONDED')
  const [updated] = await db
    .update(tickets)
    .set({
      status: nextStatus,
      assignedExpertId: guard.user.id,
      lastMessageId: message.id,
      version: ticket.version + 1,
      updatedAt: new Date(),
    })
    .where(and(eq(tickets.id, ticket.id), eq(tickets.version, ticket.version)))
    .returning()

  if (!updated) return NextResponse.json({ error: 'Ticket was updated concurrently, please retry' }, { status: 409 })

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'ticket.expert_reply',
    entityType: 'ticket',
    entityId: ticket.id,
    payload: { from: ticket.status, to: nextStatus },
  })

  // Email the line manager — fire-and-forget
  try {
    const [submitter] = await db
      .select({ email: users.email })
      .from(users)
      .where(eq(users.id, ticket.submitterId))
      .limit(1)

    if (submitter?.email && process.env.EMAIL_SERVER_HOST) {
      const [cfg] = await db
        .select({ includeResponseInEmail: tenantConfigs.includeResponseInEmail })
        .from(tenantConfigs)
        .where(eq(tenantConfigs.tenantId, ticket.tenantId))
        .limit(1)
      const includeContent = cfg?.includeResponseInEmail ?? true
      const transport = createTransport({
        host: process.env.EMAIL_SERVER_HOST,
        port: Number(process.env.EMAIL_SERVER_PORT ?? 587),
        auth: process.env.EMAIL_SERVER_USER
          ? { user: process.env.EMAIL_SERVER_USER, pass: process.env.EMAIL_SERVER_PASSWORD }
          : undefined,
      })
      const { subject, html, text } = buildResponseNotificationEmail({
        ticketReference: ticket.referenceNumber ?? ticketId,
        ticketTitle: ticket.title ?? 'Your ticket',
        responseText,
        ticketId: ticket.id,
        includeContent,
      })
      await transport.sendMail({ to: submitter.email, from: process.env.EMAIL_FROM, subject, html, text })
    }
  } catch (err) {
    console.error('[reply] Failed to send notification email:', err)
  }

  return NextResponse.json({ ticketId: ticket.id, messageId: message.id, status: updated.status })
}
